import { message } from "../../../../node_modules/antd/es/index";
import PUTPaletRoyalCanin from "../../../helpers/ordenes/PUTPaletRoyalCanin";
import { getCardStyle } from "../../../utils/getCardStyle";
import {
  extraerLoteDesdeCb2,
  extraerVtoDesdeCb1,
} from "../../../utils/getLoteVtoRaw";
import { getCardBackgroundColor } from "../../misOrdenes/omas/detalleOrden/getCardBackgroundColor";

export const calcularCapacidadPalet = (
  item,
  idpalt,
  idpala,
  tiposPalet,
  alturasPalet,
  pendiente,
  cantidadPorPalet, // opcional: valor elegido por el usuario
) => {
  if (pendiente <= 0) return 0;

  //  Si hay una capacidad manual definida, usarla (limitada por lo pendiente)
  if (cantidadPorPalet != null && cantidadPorPalet > 0) {
    return Math.min(cantidadPorPalet, pendiente);
  }

  //  Si no hay valor manual, usar la lógica original
  const ctdmax = Number(item?.ctdmax) || 0;
  const ctdplt = Number(item?.ctdplt) || 0;

  if (ctdmax > 0) {
    return Math.min(ctdmax, pendiente);
  }

  if (ctdplt > 0) {
    return Math.min(ctdplt, pendiente);
  }

  // Fallback: volumen
  const tipo = tiposPalet.find((t) => t.idpalt === idpalt);
  const altura = alturasPalet.find((a) => a.idpala === idpala);

  if (!tipo || !altura || item?.voltot <= 0 || item?.cantid <= 0) {
    return pendiente;
  }

  const voltotUnitario = item.voltot;

  const volPaletCm3 = tipo.medanc * tipo.medpro * altura.medalt;
  const volUtilM3 = volPaletCm3 / 1_000_000; // factor 1 = 100% utilidad

  const capVolumen = Math.max(1, Math.floor(volUtilM3 / voltotUnitario));

  return Math.min(capVolumen, pendiente);
};

/**
 * Determina si un palet corresponde a un artículo.
 *
 * Los artículos granel (ctdpar = 1) llegan del backend con `idartp` y `nropar`
 * en null, porque el query detallesAuditoriaSMA_QUERY los trae de un
 * `LEFT JOIN artpar` que no matchea (artículos simples no tienen partes).
 * Como en JS `null === null` es true, comparar solo por idartp/nropar hace que
 * TODOS los artículos granel coincidan entre sí y las cantidades se sumen.
 *
 * Por eso la identidad del granel se toma de `idartl`, que sí es único por
 * fila. En multiparte NO se debe usar idartl porque todas las partes de un
 * conjunto comparten el mismo idartl: ahí la identidad sigue siendo
 * idartp + nropar.
 */
export const mismoArticuloQuePalet = (item, palet) => {
  if (!item || !palet) return false;

  if (item.idartp != null) {
    return (
      palet.idartp === item.idartp &&
      (palet.nropar ?? null) === (item.nropar ?? null)
    );
  }

  const idartlItem = String(item.idartl ?? "").trim();
  if (!idartlItem) return false;

  const idartlPalet = String(palet.idartl ?? "").trim();
  if (idartlPalet) return idartlPalet === idartlItem;

  // Palet creado antes de este fix (restaurado desde ultjsn): no trae idartl,
  // se resuelve por sku.
  return String(palet.sku ?? "").trim() === String(item.sku ?? "").trim();
};

/**
 * Devuelve los palets que corresponden a un artículo.
 * @param {Array} palets Lista de palets (de cualquier origen).
 * @param {Object} item  Artículo de detallesLocal.
 * @param {Object} [opts]
 * @param {boolean} [opts.excluirLote] Si se omite, no incluye los paletLote
 *        (que se matchean aparte por sku).
 */
export const paletsDelArticulo = (palets, item, { excluirLote = true } = {}) => {
  const lista = Array.isArray(palets) ? palets : [];

  return lista.filter((p) => {
    if (excluirLote && p.esPaletLote) return false;
    return mismoArticuloQuePalet(item, p);
  });
};

export const tienePendientePaletizable = (
  paletsEnProceso,
  detallesLocal,
  palet,
) => {
  if (!palet) return false;

  const listaDetalles = Array.isArray(detallesLocal) ? detallesLocal : [];

  const item = listaDetalles.find((d) => mismoArticuloQuePalet(d, palet));
  if (!item) return false;

  const cantid = Number(item.cantid || 0);
  const ctdaudGranel = Number(item.ctdaud || 0);

  // Antes este filtro no excluía los paletLote: se mantiene igual para no
  // cambiar el cálculo de pendiente, solo se corrige la identidad del artículo.
  const totalAsignadoEnPalets = paletsDelArticulo(
    paletsEnProceso,
    item,
    { excluirLote: false },
  ).reduce((sum, p) => sum + (Number(p.asignado) || 0), 0);

  const totalAuditado = ctdaudGranel + totalAsignadoEnPalets;
  return totalAuditado < cantid;
};

// export const getTotalAuditadoYConteoPalets = (
//   paletsEnProceso,
//   item,
//   detallesLocal,
//   paletsMultiarticulo,
// ) => {
//   // Asegurar que paletsEnProceso sea un array
//   const paletsArray = Array.isArray(paletsEnProceso) ? paletsEnProceso : [];

//   let granel = 0;
//   let enPalets = 0;
//   let conteoPalets = 0;

//   // Verificar si el artículo es parte de un conjunto multipartes
//   const esMultiparte = Number(item.ctdpar) > 1;

//   if (esMultiparte && detallesLocal && item.idartf) {
//     const partesDelConjunto = detallesLocal.filter(
//       (d) => d.idartf === item.idartf,
//     );

//     const contadorPorParte = {};

//     for (const parte of partesDelConjunto) {
//       const granelParte = Number(parte.ctdaud || 0);

//       const paletsDeEstaParte = paletsArray.filter(
//         (p) =>
//           p.esMultiparte &&
//           p.partes?.some(
//             (pp) => pp.idartp === parte.idartp && pp.nropar === parte.nropar,
//           ),
//       );

//       const asignadoEnPalets = paletsDeEstaParte.reduce(
//         (sum, p) =>
//           sum + (Number(p.asignadoPorParte?.[parte.idartp] || p.asignado) || 0),
//         0,
//       );

//       contadorPorParte[parte.nropar] = granelParte + asignadoEnPalets;

//       granel += granelParte;
//       enPalets += asignadoEnPalets;
//       // ... conteoPalets si lo usás
//     }

//     if (contadorPorParte[item.nropar] !== undefined) {
//       return {
//         totalAuditado: contadorPorParte[item.nropar],
//         conteoPalets,
//       };
//     }
//   } else {
//     // Comportamiento original para artículos simples
//     const paletsDelArticulo = paletsArray.filter(
//       (p) =>
//         p.idartp === item.idartp && p.nropar === item.nropar && !p.esPaletLote,
//     );

//     // 🔹 Filtrar paletsLote del mismo SKU
//     const paletsLote = paletsArray.filter(
//       (p) => p.esPaletLote && p.sku === item.sku,
//     );

//     // 🔹 Calcular cuánto está asignado en paletsLote
//     const enPaletsLote = paletsLote.reduce(
//       (sum, p) => sum + (Number(p.asignado) || 0),
//       0,
//     );
//     const detallesMismoSku = Array.isArray(detallesLocal)
//       ? detallesLocal.filter(
//           (d) => d.sku === item.sku && (d.nropar ?? "") === (item.nropar ?? ""),
//         )
//       : [];

//     granel = detallesMismoSku.reduce(
//       (sum, d) => sum + Math.max(0, Number(d.ctdaud || 0)),
//       0,
//     );

//     // 🔹 enPalets: palets normales + paletsLote
//     enPalets =
//       paletsDelArticulo.reduce((sum, p) => sum + (Number(p.asignado) || 0), 0) +
//       enPaletsLote;

//     // 🔹 conteoPalets: contar ambos tipos con unidades > 0
//     conteoPalets =
//       paletsDelArticulo.filter((p) => (Number(p.asignado) || 0) > 0).length +
//       paletsLote.filter((p) => (Number(p.asignado) || 0) > 0).length;

//     if (Array.isArray(paletsMultiarticulo)) {
//       for (const p of paletsMultiarticulo) {
//         const articuloEnPalet = p.articulos?.find((a) => {
//           const mismoSku =
//             String(a.sku || "").trim() === String(item.sku || "").trim();

//           const mismoNropar =
//             String(a.nropar ?? "").trim() === String(item.nropar ?? "").trim();

//           return mismoSku && mismoNropar;
//         });

//         if (articuloEnPalet) {
//           const cantidadAsignada = Number(
//             articuloEnPalet.cantidad ?? articuloEnPalet.asignado ?? 0,
//           );

//           enPalets += cantidadAsignada;

//           if (cantidadAsignada > 0) {
//             conteoPalets += 1;
//           }
//         }
//       }
//     }
//   }

//   const totalAuditado = granel + enPalets;
//   return { totalAuditado, conteoPalets };
// };

export const getTotalAuditadoYConteoPalets = (
  paletsEnProceso,
  item,
  detallesLocal,
  paletsMultiarticulo,
) => {
  // Asegurar que paletsEnProceso sea un array
  const paletsArray = Array.isArray(paletsEnProceso) ? paletsEnProceso : [];

  let granel = 0;
  let enPalets = 0;
  let conteoPalets = 0;

  // Verificar si el artículo es parte de un conjunto multipartes
  const esMultiparte = Number(item.ctdpar) > 1;

  if (esMultiparte && detallesLocal && item.idartf) {
    const partesDelConjunto = detallesLocal.filter(
      (d) => d.idartf === item.idartf,
    );

    const contadorPorParte = {};

    for (const parte of partesDelConjunto) {
      const granelParte = Number(parte.ctdaud || 0);

      const paletsDeEstaParte = paletsArray.filter(
        (p) =>
          p.esMultiparte &&
          p.partes?.some(
            (pp) => pp.idartp === parte.idartp && pp.nropar === parte.nropar,
          ),
      );

      const asignadoEnPalets = paletsDeEstaParte.reduce(
        (sum, p) =>
          sum + (Number(p.asignadoPorParte?.[parte.idartp] || p.asignado) || 0),
        0,
      );

      contadorPorParte[parte.nropar] = granelParte + asignadoEnPalets;

      granel += granelParte;
      enPalets += asignadoEnPalets;
      // ... conteoPalets si lo usás
    }

    if (contadorPorParte[item.nropar] !== undefined) {
      return {
        totalAuditado: contadorPorParte[item.nropar],
        conteoPalets,
      };
    }
  } else {
    // Caso VLX / lote-vto por valcar: misma SKU pero filas separadas
    if (item?.valcar) {
      const valcarItem = String(item.valcar ?? "").trim();

      const detallePorValcar = Array.isArray(detallesLocal)
        ? detallesLocal.find(
            (d) => String(d.valcar ?? "").trim() === valcarItem,
          )
        : null;

      granel = Number(detallePorValcar?.ctdaud || 0);

      return {
        totalAuditado: granel,
        conteoPalets: 0,
      };
    }

    // Comportamiento original para artículos simples
    const paletsDelArticuloLocal = paletsDelArticulo(paletsArray, item);

    // 🔹 Filtrar paletsLote del mismo SKU
    const paletsLote = paletsArray.filter(
      (p) => p.esPaletLote && p.sku === item.sku,
    );

    // 🔹 Calcular cuánto está asignado en paletsLote
    const enPaletsLote = paletsLote.reduce(
      (sum, p) => sum + (Number(p.asignado) || 0),
      0,
    );

    const detallesMismoSku = Array.isArray(detallesLocal)
      ? detallesLocal.filter(
          (d) => d.sku === item.sku && (d.nropar ?? "") === (item.nropar ?? ""),
        )
      : [];

    granel = detallesMismoSku.reduce(
      (sum, d) => sum + Math.max(0, Number(d.ctdaud || 0)),
      0,
    );

    // 🔹 enPalets: palets normales + paletsLote
    enPalets =
      paletsDelArticuloLocal.reduce((sum, p) => sum + (Number(p.asignado) || 0), 0) +
      enPaletsLote;

    // 🔹 conteoPalets: contar ambos tipos con unidades > 0
    conteoPalets =
      paletsDelArticuloLocal.filter((p) => (Number(p.asignado) || 0) > 0).length +
      paletsLote.filter((p) => (Number(p.asignado) || 0) > 0).length;

    if (Array.isArray(paletsMultiarticulo)) {
      for (const p of paletsMultiarticulo) {
        const articuloEnPalet = p.articulos?.find((a) => {
          const mismoSku =
            String(a.sku || "").trim() === String(item.sku || "").trim();

          const mismoNropar =
            String(a.nropar ?? "").trim() === String(item.nropar ?? "").trim();

          return mismoSku && mismoNropar;
        });

        if (articuloEnPalet) {
          const cantidadAsignada = Number(
            articuloEnPalet.cantidad ?? articuloEnPalet.asignado ?? 0,
          );

          enPalets += cantidadAsignada;

          if (cantidadAsignada > 0) {
            conteoPalets += 1;
          }
        }
      }
    }
  }

  const totalAuditado = granel + enPalets;
  return { totalAuditado, conteoPalets };
};

/*
=================================================================
ORIGINAL — comentada, NO se usa. Se conserva para comparar.
El bug: en "Paso 3" el valorActual se calculaba con paletsDelArticulo(),
que internamente descarta los palets esPaletLote (excluirLote por defecto).
En auditorías de lote TODOS los palets son esPaletLote, así que la suma
daba 0 y la card caía en el casi-blanco #fffffff2 aunque las unidades
estuvieran contadas. El número de la card no pasaba por acá (lo calcula
getTotalAuditadoYConteoPalets), por eso se veía el número bien y el
color en blanco.

export const getColorEstado = (
  paletsEnProceso,
  item,
  darkMode,
  detallesLocal,
  paletsMultiarticulo,
) => {
  // Paso 1: ¿Esta pieza está en algún palet multiartículo?
  const paletMultiConPieza = paletsEnProceso.find(
    (p) =>
      p.esMultiparte && p.partes?.some((parte) => mismoArticuloQuePalet(item, parte)),
  );

  if (paletMultiConPieza && detallesLocal && item.idartf) {
    const partesDelConjunto = detallesLocal.filter(
      (d) => d.idartf === item.idartf,
    );
    let totalActualConjunto = 0;
    let totalEsperadoConjunto = 0;

    for (const parte of partesDelConjunto) {
      const granel = Number(parte.ctdaud ?? 0);
      const esperado = Number(parte.cantid ?? 0);

      // BUSCAR EL PALET MULTIPARTE CORRECTO
      const paletMulti = paletsEnProceso.find(
        (p) =>
          p.esMultiparte &&
          p.partes?.some((partePalet) =>
            mismoArticuloQuePalet(parte, partePalet),
          ),
      );

      const enPalets = paletMulti
        ? Number(
            paletMulti.asignadoPorParte?.[parte.idartp] || paletMulti.asignado,
          ) || 0
        : 0;

      totalActualConjunto += granel + enPalets;
      totalEsperadoConjunto += esperado;
    }

    const color = getCardBackgroundColor(
      totalActualConjunto,
      totalEsperadoConjunto,
    );

    if (color !== "transparent") return color;
  }

  // Paso 3: En todos los demás casos → evaluamos SOLO esta pieza
  const valorGranel = Number(item.ctdaud ?? 0);

  const valorEsperado = Number(item.cantid ?? 0);
  const valorEnPalets = paletsDelArticulo(paletsEnProceso, item).reduce(
    (sum, p) => sum + (Number(p.asignado) || 0),
    0,
  );

  let valorEnPaletsMulti = 0;

  if (Array.isArray(paletsMultiarticulo)) {
    for (const p of paletsMultiarticulo) {
      const articuloEnPalet = p.articulos?.find((a) =>
        mismoArticuloQuePalet(item, a),
      );

      if (articuloEnPalet) {
        valorEnPaletsMulti += Number(articuloEnPalet.cantidad || 0);
      }
    }
  }
  const valorActual = valorGranel + valorEnPalets + valorEnPaletsMulti;

  const color = getCardBackgroundColor(valorActual, valorEsperado);
  if (color !== "transparent") return color;

  const base = getCardStyle(darkMode);
  return base?.backgroundColor ?? "#ffffff";
};
=================================================================
*/

/**
 * Color de la card por estado de conteo.
 *
 * El cálculo es el original, más un único término nuevo: los palets de lote
 * del mismo artículo, agregados por `sku`.
 *
 * Por qué ese término: en una auditoría de lote TODOS los palets son
 * esPaletLote, y paletsDelArticulo() los descarta por defecto (excluirLote).
 * La suma daba 0 y la card caía en el casi-blanco #fffffff2 aunque las
 * unidades estuvieran contadas.
 *
 * Por qué NO se reutiliza getTotalAuditadoYConteoPalets (que ya suma los
 * palets de lote) para reemplazar el valorActual: esa función matchea en tres
 * puntos distinto al color, y delegar en ella cambiaba el color de tres
 * escenarios que antes funcionaban — los artículos con `valcar` de VLX (esa
 * rama ignora los palets) y los palets multiparte restaurados de `ultjsn`
 * sin `sku` (exige sku+nropar en vez de idartp). Agregar el término no puede
 * restar: es una suma no negativa, así que jamás devuelve una card a blanco.
 */
export const getColorEstado = (
  paletsEnProceso,
  item,
  darkMode,
  detallesLocal,
  paletsMultiarticulo,
) => {
  // Paso 1: ¿Esta pieza está en algún palet multiartículo?
  const paletMultiConPieza = paletsEnProceso.find(
    (p) =>
      p.esMultiparte && p.partes?.some((parte) => mismoArticuloQuePalet(item, parte)),
  );

  if (paletMultiConPieza && detallesLocal && item.idartf) {
    const partesDelConjunto = detallesLocal.filter(
      (d) => d.idartf === item.idartf,
    );
    let totalActualConjunto = 0;
    let totalEsperadoConjunto = 0;

    for (const parte of partesDelConjunto) {
      const granel = Number(parte.ctdaud ?? 0);
      const esperado = Number(parte.cantid ?? 0);

      // BUSCAR EL PALET MULTIPARTE CORRECTO
      const paletMulti = paletsEnProceso.find(
        (p) =>
          p.esMultiparte &&
          p.partes?.some((partePalet) =>
            mismoArticuloQuePalet(parte, partePalet),
          ),
      );

      const enPalets = paletMulti
        ? Number(
            paletMulti.asignadoPorParte?.[parte.idartp] || paletMulti.asignado,
          ) || 0
        : 0;

      totalActualConjunto += granel + enPalets;
      totalEsperadoConjunto += esperado;
    }

    const color = getCardBackgroundColor(
      totalActualConjunto,
      totalEsperadoConjunto,
    );

    if (color !== "transparent") return color;
  }

  // Paso 3: En todos los demás casos → evaluamos SOLO esta pieza
  //
  // El granel suma TODAS las filas del mismo sku+nropar: en auditoría lote/VLX
  // cada lote es una fila (con lote/vto, oculta en "Todos"), y el +1 cae ahí,
  // no en la fila visible. Antes la fila visible quedaba en 0 → card blanca
  // con unidades contadas. Mismo criterio que el número (getTotalAuditadoYConteoPalets).
  const valorGranel = Array.isArray(detallesLocal)
    ? detallesLocal
        .filter(
          (d) => d.sku === item.sku && (d.nropar ?? "") === (item.nropar ?? ""),
        )
        .reduce((sum, d) => sum + Math.max(0, Number(d.ctdaud || 0)), 0)
    : Number(item.ctdaud ?? 0);

  const valorEsperado = Number(item.cantid ?? 0);
  const valorEnPalets = paletsDelArticulo(paletsEnProceso, item).reduce(
    (sum, p) => sum + (Number(p.asignado) || 0),
    0,
  );

  let valorEnPaletsMulti = 0;

  if (Array.isArray(paletsMultiarticulo)) {
    for (const p of paletsMultiarticulo) {
      const articuloEnPalet = p.articulos?.find((a) =>
        mismoArticuloQuePalet(item, a),
      );

      if (articuloEnPalet) {
        valorEnPaletsMulti += Number(articuloEnPalet.cantidad || 0);
      }
    }
  }

  // ÚNICO TÉRMINO NUEVO — los palets de lote del mismo artículo, agregados.
  //
  // paletsDelArticulo() excluye los esPaletLote (excluirLote por defecto), así
  // que acá no hay doble conteo: son conjuntos disjuntos.
  //
  // El match es por `sku` y no por idartl a propósito: en un palet-lote la
  // identidad real del palet es idartl+lote, y el mismo artículo puede tener
  // varios palets repartidos entre lotes. Para el color de la card de "Todos"
  // —que a propósito no muestra lote ni vto, para no solaparse con la otra
  // pestaña— lo que importa es la sumatoria TOTAL de ese artículo contra su
  // `cantid`, que es el total entre todos sus lotes. Da igual si el artículo
  // tiene un palet o cinco.
  const valorEnPaletsLote = (
    Array.isArray(paletsEnProceso) ? paletsEnProceso : []
  )
    .filter((p) => p.esPaletLote && item.sku && p.sku === item.sku)
    .reduce((sum, p) => sum + (Number(p.asignado) || 0), 0);

  const valorActual =
    valorGranel + valorEnPalets + valorEnPaletsMulti + valorEnPaletsLote;

  const color = getCardBackgroundColor(valorActual, valorEsperado);
  if (color !== "transparent") return color;

  const base = getCardStyle(darkMode);
  return base?.backgroundColor ?? "#ffffff";
};

// Una vez realizada la carga del form modal para nuevo palet lote
//  se ejecuta esta función para leer los códigos de barra
//  y obtener la información del lote vto desde el backend.
//  Devuelve un objeto con la información del palet o null si hubo un error.

export const handleLeerCodigosLote = async (idauds, data) => {
  const result = await PUTPaletRoyalCanin(idauds, data.cb1, data.cb2, data.cb3);

  if (result && result.status === 200) {
    const palet = result?.data?.paljsn;

    if (!palet?.idpala || !palet?.idpalt) {
      message.error("No se pudo determinar el tipo y altura del palet");
      return null;
    }

    const vto = extraerVtoDesdeCb1(data.cb1);
    const lote = extraerLoteDesdeCb2(data.cb2);

    return {
      ...palet,
      cb1: data.cb1,
      cb2: data.cb2,
      cb3: data.cb3,
      lote,
      vto,
    };
  } else {
    message.error(result?.data?.message);
    return null;
  }
};
